<template>
  <div class="shop-product-details">
    <div class="shop-product-details-inner">
      <Images
        :value="images"
        :title="item.product.name"
        :autoplay="autoplayImages"
        :startIndex="imageStartIndex"
        @index="onImageIndexUpdate"
        class="shop-product-details__images"
      />

      <div class="shop-product-details__content">
        <div class="shop-product-details__text">
          <h2 class="shop-product-details__name">{{ item.product.name }}</h2>
          <ReadMore
            :key="item.id"
            v-if="item.product.description"
            class="shop-product-details__description"
            :value="item.product.description"
            :limit="5"
          />
          <ButtonComponent
            v-if="item.acceptInquiries && accessToken && features.includes('inquiry')"
            :label="`${$tc('new')} ${$tc('inquiry').toLowerCase()}`"
            :icon="['fal', 'comment']"
            class="shop-product-details__inquiry"
            @click.stop="inquiry"
          />
        </div>

        <PropertyChanges
          v-if="showPropertyChanges"
          :value="item.referencePropertyChanges"
          variant="long"
          class="shop-product-details__property-changes"
        />

        <Collapsible
          v-if="collapsibleItems.length"
          :items="collapsibleItems"
          :initial="['variants', 'floorplan', 'bundle']"
          class="shop-product-details__metadata"
        >
          <template #variants="{ item }">
            <Variants
              :value="item.variant"
              :items="item.data.items"
              :category="item.data.category"
              :readonly="isReadonly"
              @select="internalId => $emit('select', { id: item.product.id, variant: internalId })"
            >
              <template #description="{ current }">
                <ReadMore :value="current.description" :limit="6"
              /></template>
            </Variants>
          </template>
          <template #technical="{ item }">
            <ReadMore :value="item.data" :limit="6" />
          </template>
          <template #attachments="{ item }">
            <FileComponent :value="item.data" readonly class="shop-product-details__attachments" />
          </template>
          <template #bundle>
            <div class="shop-product-details__bundle" v-if="$path('product.products.length', item)">
              <ProductCard
                v-for="(product, index) in item.product.products"
                :key="index"
                :value="{ product: product }"
                @click="bus.$emit('product', { type: 'bundle', data: { product } })"
              />
            </div>
          </template>
          <template #floorplan>
            <Floorplan
              v-if="Boolean(collapsibleItems.find(({ id }) => id === 'floorplan'))"
              :image="floorplan.image.url"
              :value="floorplan.data"
            />
          </template>
        </Collapsible>
      </div>
    </div>
  </div>
</template>

<script>
import { ButtonComponent } from 'vue-elder-button'
import { FileComponent } from '@kvass/file'
import ProductCard from './Card'
import Images from './Images'
import Collapsible from '../components/Collapsible'
import Variants from '../components/Variants.vue'
import Floorplan from '../Floorplan'
import ReadMore from '../components/ReadMore.vue'
import PropertyChanges from '../components/PropertyChanges'

import { getFloorplan, getItem, getContextualImages } from '../utils'

export default {
  inject: {
    shop: { default: null },
    bus: { default: null },
    getItemStatus: { default: null },
    accessToken: { default: null },
  },
  provide() {
    return {
      shop: this.shop,
    }
  },
  props: {
    value: Object,
    subcategory: Object,
    features: {
      type: Array,
      default: () => ['variants', 'bundle', 'inquiry', 'property-changes'],
    },
    showFloorplan: {
      type: Boolean,
      default: true,
    },
    readonly: Boolean,
  },
  watch: {
    value: {
      handler: 'setStartIndex',
      immediate: true,
    },
  },
  data() {
    return {
      imageStartIndex: 0,
    }
  },
  computed: {
    showPropertyChanges() {
      return (
        this.$path('item.referencePropertyChanges.length') &&
        this.features.includes('property-changes')
      )
    },
    imageSyncGroup() {
      return this.$path('subcategory.metadata.imageSettings.syncslide')
    },
    autoplayImages() {
      return this.$path('subcategory.metadata.imageSettings.autoslide')
    },
    isReadonly() {
      if (this.readonly) return true
      if (!this.getItemStatus) return false
      return !this.getItemStatus(this.value.id).isUnselectable
    },
    floorplan() {
      if (!this.item.id) return

      return getFloorplan(this.item, this.shop)
    },
    images() {
      return [...this.contextualImages, ...this.item.product.images]
    },
    contextualImages() {
      if (!this.shop || !this.item) return []
      return getContextualImages(this.shop, this.item)
    },

    collapsibleItems() {
      const base = { product: this.item }

      const { product, variant } = this.item
      const {
        attachments = [],
        technicalDescription,
        variants = [],
        variant: productVariant,
      } = product

      return [
        {
          id: 'variants',
          variant: variant || productVariant,
          label: this.$tc('variants', 1),
          data: variants,
          condition: this.features.includes('variants') && variants && variants.length,
          ...base,
        },
        {
          id: 'attachments',
          label: this.$tc('attachment', 1),
          data: attachments,
          condition: attachments.length,
          ...base,
        },
        {
          id: 'technical',
          label: this.$t('technicalDescription'),
          data: technicalDescription,
          condition: Boolean(technicalDescription),
          ...base,
        },
        {
          id: 'bundle',
          label: this.$tc('productBundle'),
          condition: Boolean(
            this.features.includes('bundle') && this.$path('item.product.products.length'),
          ),
          ...base,
        },
        {
          id: 'floorplan',
          label: this.$t('youAreHere'),
          condition: Boolean(this.floorplan),
          ...base,
        },
      ].filter(({ condition = true }) => condition)
    },
    item() {
      return (
        this.value || {
          product: {
            name: this.$t('noShopDefaultProduct'),
            images: [],
            attachments: [],
          },
        }
      )
    },
  },
  methods: {
    setStartIndex() {
      this.imageStartIndex = parseInt(
        window.sessionStorage.getItem(`imagesync.${this.imageSyncGroup}`) || 0,
      )
    },
    onImageIndexUpdate(i) {
      if (!this.imageSyncGroup) return
      window.sessionStorage.setItem(`imagesync.${this.imageSyncGroup}`, i)
    },
    inquiry() {
      const { internalId, name } = this.value.product
      const { category, subcategory } = getItem(this.value.id, this.shop)

      this.bus.$emit('inquiry', {
        scoped: true,
        title: [category.label, subcategory.label, name + (internalId ? ` (${internalId})` : '')]
          .filter(Boolean)
          .join(' > '),
      })
    },
  },
  components: {
    ButtonComponent,
    FileComponent,
    ProductCard,
    Collapsible,
    Variants,
    ReadMore,
    Floorplan,
    PropertyChanges,
    Images,
  },
}
</script>

<style lang="scss">
@import '../mixins';

.shop-product-details {
  container-type: inline-size;

  display: flex;
  flex-direction: column;

  &-inner {
    flex-grow: 1;

    display: grid;
    grid-template-columns: 1fr 400px;

    grid-gap: 1rem 3rem;
    align-items: start;

    grid-template-areas: 'images content';

    max-height: calc(100dvh - var(--shop-header-height) - var(--shop-spacing) * 2);
    overflow-y: auto;

    $components: 'text', 'description', 'metadata', 'images', 'actions', 'property-changes',
      'content';

    @each $component in $components {
      .shop-product-details__#{$component} {
        grid-area: #{$component};
      }
    }

    &:not(:has(.shop-collapsible)) {
      grid-template-areas: 'images images' 'text property-changes';
      grid-template-rows: 1fr auto;

      .shop-product-details__content {
        display: contents;
      }

      .shop-product-details__images {
        flex: 1 0;
      }

      &:has(.shop-product-details__content) .shop-product-details__images {
        // border-bottom: 1px solid var(--border-color);
      }
    }

    @container (max-width: 1000px) {
      display: flex;
      flex-direction: column;
      align-items: stretch;

      $order: 'images', 'text', 'description', 'actions', 'property-changes', 'metadata';

      @for $i from 1 through length($order) {
        .shop-product-details__#{nth($order, $i)} {
          order: $i;
        }
      }

      .shop-product-details__content {
        display: contents;
      }

      .shop-product-details__images {
        flex: 1 0;
      }
    }
  }

  &__bundle {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .shop-product-card {
      background-color: white;
      border: 1px solid var(--border-color);
      padding: 0.5rem;
      border-radius: var(--border-radius);

      &__price,
      &__variant {
        display: none;
      }

      &__image {
        border: none;
      }

      &:hover {
        background-color: rgba(black, 0.005);
      }
    }
  }

  .shop-product-details__images {
    align-self: stretch;

    max-height: calc(100dvh - var(--shop-header-height, 90px) - var(--shop-spacing, 2rem) * 2);
  }

  &__inquiry {
    align-self: flex-start;
    font-size: 0.8em;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    max-height: calc(100dvh - var(--shop-header-height, 90px) - var(--shop-spacing, 2rem) * 2);
    overflow-y: auto;
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__name {
    margin: 0;
    line-height: 1;
  }

  &__description {
    // max-width: 65ch;

    li {
      padding-inline-start: 0.2em;

      p {
        margin: 0;
      }

      & + li {
        margin-top: 0.2em;
      }
    }

    ul {
      padding-inline-start: 1em;
    }

    & p:first-child {
      margin-block-start: 0;
    }

    & p:last-child {
      margin-block-end: 0;
    }
  }

  &__attachments {
    position: relative;
    &-wrapper {
      height: 100%;
      max-height: 250px;
      overflow-y: auto;
    }
  }
}
</style>
